import type { StateKey, Type, ViewContainerRef } from '@angular/core';
import {
    DOCUMENT,
    inject,
    Injectable,
    Injector,
    makeStateKey,
    PendingTasks,
    signal,
    TransferState,
} from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { switchMapTo, tap } from 'rxjs/operators';

import type { IFooterSettings } from 'components/organisms/footer/entities/types';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';
import type { TFullColorScheme } from 'src/shared/types/color-schemes-and-themes';
import { EProductType } from 'src/shared/types/product-type';

import { LayoutDataService } from './layout-data.service';
import type { IDefaultLayout, IMenuItem, THeaderType } from '../entities/types';

const AVAILABLE_FOOTER_TYPES = ['full', 'light'] as const;

interface ILoadedModule {
    component: Type<unknown>;
}

export type TAvailableFooterTypes = (typeof AVAILABLE_FOOTER_TYPES)[number];

// previously SeparatedLayoutService from FE Platform
@Injectable({ providedIn: 'root' })
export class LayoutService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly transferState = inject(TransferState);
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly layoutDataService = inject(LayoutDataService);
    private readonly injector = inject(Injector);
    private readonly pendingTasks = inject(PendingTasks);

    /**
     * Footer modules are loaded lazily on first render (footers are below the fold).
     * Cached per key so a module is fetched at most once per variant/product combination.
     */
    private readonly footerModuleCache = new Map<string, Promise<ILoadedModule>>();
    private renderedFooterType: TAvailableFooterTypes | undefined;
    private renderedFooterSecondary = false;
    private readonly footerTypeKey: StateKey<string> = makeStateKey('footerType');

    private readonly isArticle = signal<boolean>(false);

    public setFooterData(data: IFooterSettings) {
        this.layoutDataService.setFooterData(data);
    }

    public getFooterData(): IFooterSettings | null {
        return this.layoutDataService.getFooterData();
    }

    public setFooterSecondaryData(data: IMenuItem[] | null): void {
        this.layoutDataService.setFooterSecondaryData(data);
    }

    public getFooterSecondaryData(): IMenuItem[] | null {
        return this.layoutDataService.getFooterSecondaryData();
    }

    public setHeaderData(data: IMenuItem[] | null) {
        this.layoutDataService.setHeaderData(data);
    }

    public getHeaderData(): IMenuItem[] | null {
        return this.layoutDataService.getHeaderData();
    }

    public setHeaderType(type: THeaderType | null): void {
        this.layoutDataService.setHeaderType(type);
    }

    public shouldRenderHeader(): boolean {
        return this.layoutDataService.getHeaderType() === 'full';
    }

    public setIsArticle(value: boolean): void {
        this.isArticle.set(value);
    }

    public getIsArticle(): boolean {
        return this.isArticle();
    }

    public fetchDefaultLayoutOnError(): Observable<IDefaultLayout> {
        return this.restService.getDefaultPageLayout().pipe(
            tap((data) => {
                if (data?.main_navigation) {
                    this.layoutDataService.setHeaderType('full');
                    this.layoutDataService.setHeaderData(data.main_navigation);
                }
                if (data?.footer_menu) {
                    const currentFooterData = this.layoutDataService.getFooterData();
                    this.layoutDataService.setFooterData({ ...currentFooterData, menu_data: data.footer_menu });
                }
            }),
            // removing the response data or else the PageView will try to render itself based on it
            switchMapTo(of(null))
        );
    }

    /**
     * Defers the footer render to a macrotask to reduce TBT, but keeps the application
     * unstable until it completes. The app is zoneless, so neither the timer nor the lazy
     * footer import blocks stability on its own, and SSR would serialize the page without
     * a footer. Temporary until the footer is rendered declaratively.
     * TODO: https://www.wrike.com/open.htm?id=4514425236
     */
    public scheduleFooterRender(
        container: ViewContainerRef,
        variant: TAvailableFooterTypes,
        productType: EProductType,
        lastWidgetColor?: TFullColorScheme
    ): void {
        this.pendingTasks.run(async () => {
            await new Promise<void>((resolve) => setTimeout(resolve));

            try {
                await this.renderFooterTo(container, variant, productType, lastWidgetColor);
            } catch (error) {
                console.error('Failed to render footer:', error);
            }
        });
    }

    public async renderFooterTo(
        container: ViewContainerRef,
        variant: TAvailableFooterTypes = 'full',
        productType: EProductType,
        lastWidgetColor?: TFullColorScheme
    ): Promise<void> {
        if (this.renderedFooterType === variant) {
            return;
        }

        if (AVAILABLE_FOOTER_TYPES.indexOf(variant) === -1) {
            throw new Error(
                `Invalid footer variant: ${variant}. Available variants: "${AVAILABLE_FOOTER_TYPES.join('", "')}"`
            );
        }

        const previousFooterType = this.renderedFooterType;
        this.renderedFooterType = variant;

        if (!this.transferState.hasKey(this.footerTypeKey)) {
            this.transferState.set(this.footerTypeKey, variant);
        }

        try {
            const { component } = await this.getFooterModule(variant, productType);

            if (previousFooterType !== undefined) {
                container.clear();
            }

            const ref = container.createComponent(component);

            // Components created via ViewContainerRef.createComponent() don't get hydration data
            // serialized on the server (https://github.com/angular/angular/issues/61231), so the
            // footer's internal `@defer (hydrate on viewport)` block would never trigger on the
            // client. Skipping hydration makes the client fully re-render the footer instead.
            // TODO: https://www.wrike.com/open.htm?id=4514425236
            ref.location.nativeElement.setAttribute('ngSkipHydration', '');

            ref.setInput('colorScheme', this.getFooterColorScheme(lastWidgetColor));
        } catch (error) {
            this.renderedFooterType = previousFooterType;
            throw error;
        }
    }

    public async renderFooterSecondaryTo(
        container: ViewContainerRef,
        colorScheme: TFullColorScheme = 'white'
    ): Promise<void> {
        container.clear();

        const { component } = await this.loadFooterSecondary();
        const ref = container.createComponent(component);

        ref.setInput('colorScheme', colorScheme);
        this.renderedFooterSecondary = true;
    }

    public clearFooterSecondary(container: ViewContainerRef): void {
        if (!this.renderedFooterSecondary && container.length === 0) {
            return;
        }

        container.clear();
        this.renderedFooterSecondary = false;
    }

    private getFooterColorScheme(lastWidgetColor: TFullColorScheme): TFullColorScheme {
        if (lastWidgetColor === 'collaborate-blue') {
            return 'midnight';
        }

        if (lastWidgetColor === 'collaborate-white') {
            return 'white';
        }

        return lastWidgetColor;
    }

    /**
     * Lazily loads (and caches) the footer module for the given variant/product.
     * Klaxoon products always use the Klaxoon footer. Failed loads are evicted from
     * the cache so subsequent renders can retry.
     */
    private getFooterModule(variant: TAvailableFooterTypes, productType: EProductType): Promise<ILoadedModule> {
        const isKlaxoon = productType === EProductType.Klaxoon;
        const cacheKey = `${isKlaxoon ? 'klaxoon' : 'default'}:${variant}`;

        let modulePromise = this.footerModuleCache.get(cacheKey);

        if (!modulePromise) {
            modulePromise = (
                isKlaxoon
                    ? this.loadKlaxoonFooter(this.injector)
                    : variant === 'light'
                      ? this.loadLightFooter(this.injector)
                      : this.loadFooter(this.injector)
            ).catch((err) => {
                this.footerModuleCache.delete(cacheKey);
                return Promise.reject(err);
            });

            this.footerModuleCache.set(cacheKey, modulePromise);
        }

        return modulePromise;
    }

    private loadFooter(injector: Injector): Promise<ILoadedModule> {
        return Promise.all([
            import('components/organisms/footer/footer.component').then((module) => module.FooterComponent),
        ]).then((modules) => this.loadComponentAndModule(modules, injector));
    }

    private loadLightFooter(injector: Injector): Promise<ILoadedModule> {
        return Promise.all([
            import('components/organisms/light-footer/light-footer.component').then(
                (module) => module.LightFooterComponent
            ),
        ]).then((modules) => this.loadComponentAndModule(modules, injector));
    }

    private loadKlaxoonFooter(injector: Injector): Promise<ILoadedModule> {
        return Promise.all([
            import('components/organisms/klaxoon-footer/klaxoon-footer.component').then(
                (module) => module.KlaxoonFooterComponent
            ),
        ]).then((modules) => this.loadComponentAndModule(modules, injector));
    }

    private loadFooterSecondary(): Promise<ILoadedModule> {
        return Promise.all([
            import('components/organisms/footer-secondary/footer-secondary.component').then(
                (module) => module.FooterSecondaryComponent
            ),
        ]).then((modules) => this.loadComponentAndModule(modules));
    }

    private loadComponentAndModule = async ([component, ..._], _injector?: Injector): Promise<ILoadedModule> => {
        return {
            component,
        };
    };
}
